import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BellOff } from 'lucide-react';
import { MarkReadButton } from './MarkReadButton';
import { SwipeToRead } from './SwipeToRead';
import { useListingDetail } from '../../hooks/useListings';
import { useMarkActivitiesRead } from '../../hooks/useActivities';
import { useActivityListStore } from '../../store/useActivityListStore';
import { useProfileStore } from '../../store/useProfileStore';
import { getListingAvailability } from '../../utils/listingAvailability';
import type { RecipientActivity } from '../../types/activity';

/** How long the card must stay continuously on screen before it counts as
 *  "seen" and consumes itself. Long enough that a fast scroll past doesn't
 *  silently eat a notification, short enough to land while the user is still
 *  looking at the card. */
const SEEN_DWELL_MS = 1500;

/** Must match the collapse transition duration below — the row is pulled from
 *  the store only after the animation has finished playing. */
const EXIT_MS = 350;

/** Fraction of the card that must be in view for the dwell clock to run. */
const VISIBLE_RATIO = 0.6;

interface ActivityListingStatusCardProps {
  activity: RecipientActivity;
  /** True when rendered in the Unread tab — gates the dismiss X and the
   *  auto-read behaviour (a card already in Read has nothing to consume). */
  unread: boolean;
  /** Manual dismiss (the X). Marks read and reloads the list, as before. */
  onDismiss: (activityId: string) => void;
  /** Opens the per-chef snooze chooser. Only offered on connection-dish cards. */
  onSnooze: (target: { listerId: string; listerName?: string }) => void;
}

function getListingMeta(activity: RecipientActivity): Record<string, unknown> | undefined {
  return (activity.metadata as Record<string, unknown> | undefined)?.listing as
    | Record<string, unknown>
    | undefined;
}

/**
 * Pinned listing-lifecycle card (listing went live / ended / a connection just
 * posted a dish), rendered at the top of its listing group under the
 * DishInfoCard header.
 *
 * The activity's metadata is a snapshot from publish time, so a "X just posted
 * Y" card can't tell on its own that the dish has since sold out — which is
 * exactly the lingering-notification complaint. The card therefore reads the
 * listing's CURRENT state via useListingDetail and, when the dish is no longer
 * orderable, goes stale (grayscale + a status pill), marks itself read once
 * seen, and collapses out. Nothing is deleted: the card is still there in the
 * Read tab.
 */
export const ActivityListingStatusCard: React.FC<ActivityListingStatusCardProps> = ({
  activity,
  unread,
  onDismiss,
  onSnooze,
}) => {
  const { t, i18n } = useTranslation('activity');
  const listingMeta = getListingMeta(activity);
  const listingId = listingMeta?.listingId as string | undefined;
  const isConnectionDish = listingMeta?.connectionDish === true;
  const listerId = listingMeta?.listerId as string | undefined;
  const listerName = listingMeta?.listerDisplayName as string | undefined;

  // Only connection-dish cards ("X just posted Y") chase live status. The
  // lister's own listing_live/expired cards deliberately stay put until they
  // dismiss them, so an ended listing always surfaces its own card — going
  // stale and auto-consuming those would take that away.
  const { data: listing, isError, error } = useListingDetail(
    isConnectionDish ? listingId : undefined,
    // A deleted listing is handled here (`gone` below) — no error modal.
    { quiet404: true },
  );
  const markRead = useMarkActivitiesRead();

  const rootRef = useRef<HTMLDivElement | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [exiting, setExiting] = useState(false);
  const consumedRef = useRef(false);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // The exit timer outlives the observer effect (it's armed from inside a
  // fired dwell), so it gets its own unmount cleanup — closing the sheet
  // mid-collapse must not leave a timer pointing at a torn-down store slice.
  useEffect(() => () => clearTimeout(exitTimerRef.current), []);

  // A deleted listing 404s. That's still "you can't order this any more", so it
  // gets the generic Unavailable pill rather than an error state. Any other
  // failure (offline, 5xx) leaves the card untouched — better a stale card than
  // one wrongly greyed out and auto-consumed.
  const gone = isError && (error as Error & { status?: number } | null)?.status === 404;

  // Shares getListingAvailability with DishInfoCard and the listing detail
  // CTA, so the activity centre can't disagree with them about why something
  // can't be ordered. A 404 (`gone`) outranks the payload's own reason.
  const { unavailable: listingUnavailable, reason: unavailableReason } =
    getListingAvailability(listing);
  const isUnavailable = isConnectionDish && (gone || listingUnavailable);

  const staleLabel =
    !gone && unavailableReason === 'orders_closed'
      ? t('card.staleOrdersClosed')
      : !gone && unavailableReason === 'sold_out'
        ? t('card.staleSoldOut')
        : t('card.staleUnavailable');

  // Auto-read: run a dwell clock while the card is on screen, and consume it
  // once the clock completes. The observer is torn down as soon as the card is
  // consumed so a re-entry can't fire a second mark-read.
  useEffect(() => {
    if (!unread || !isUnavailable || consumedRef.current) return;
    const el = rootRef.current;
    if (!el) return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= VISIBLE_RATIO) {
          // Grey out the moment it comes into view; the dwell only gates the read.
          setRevealed(true);
          if (timer) return;
          timer = setTimeout(() => {
            if (consumedRef.current) return;
            consumedRef.current = true;
            observer.disconnect();
            const profileId = useProfileStore.getState().activeProfile?.id;
            if (profileId) {
              markRead.mutate({ profileId, activityIds: [activity.id] });
            }
            setExiting(true);
            exitTimerRef.current = setTimeout(() => {
              useActivityListStore.getState().consumeActivity(activity.id);
            }, EXIT_MS);
          }, SEEN_DWELL_MS);
        } else if (timer) {
          // Scrolled back out before the dwell completed — restart the clock on
          // the next entry rather than crediting partial time.
          clearTimeout(timer);
          timer = undefined;
        }
      },
      { threshold: [0, VISIBLE_RATIO] },
    );
    observer.observe(el);
    return () => {
      if (timer) clearTimeout(timer);
      observer.disconnect();
    };
    // markRead is a stable mutation object from TanStack; excluded so a
    // re-render mid-dwell can't tear down and restart the clock.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unread, isUnavailable, activity.id]);

  // In the Read tab there's nothing to wait for, so the card renders already
  // stale. The reveal transition belongs to the one moment it means something
  // (a card going stale under the user in Unread, on its way out); replaying it
  // every time they revisit the same card in Read is just noise.
  const showStale = isUnavailable && (revealed || !unread);
  const animateReveal = unread && revealed;

  // Desaturate + dim the parts of the card the stale listing invalidates. Kept
  // off the card root so the status pill — the one piece of NEW information —
  // keeps its full-strength red; `filter` can't be undone by a descendant, and
  // greying the pill is exactly what would make it easy to miss.
  const staleClass = showStale ? 'hp-activity-stale' : '';

  return (
    <SwipeToRead
      onCommit={() => onDismiss(activity.id)}
      label={t('card.markAsRead')}
      disabled={!unread}
    >
    <div
      ref={rootRef}
      className={[
        'hp-activity-collapse overflow-hidden',
        exiting ? 'max-h-0 opacity-0' : 'max-h-[400px] opacity-100',
      ].join(' ')}
    >
      <div className="relative rounded-lg border border-border bg-muted/40 px-4 py-3">
        <p className={['pr-9 text-sm font-medium text-foreground', staleClass].filter(Boolean).join(' ')}>
          {activity.message}
        </p>

        <div className="mt-0.5 flex flex-wrap items-center gap-2">
          <span className={['text-xs text-muted-foreground', staleClass].filter(Boolean).join(' ')}>
            {new Date(activity.createdAt).toLocaleString(i18n.language)}
          </span>
          {showStale && (
            <span
              aria-live="polite"
              className={[
                'inline-flex items-center rounded-full bg-brand-danger px-2 py-0.5 text-xs font-semibold text-white',
                animateReveal ? 'hp-activity-stale-pill' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {staleLabel}
            </span>
          )}
        </div>

        {isConnectionDish && listerId && (
          <button
            type="button"
            onClick={() => onSnooze({ listerId, listerName })}
            className={[
              'mt-2 flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-brand-primary',
              staleClass,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <BellOff className="h-3.5 w-3.5" />
            {t('card.snooze')}
          </button>
        )}

        {unread && (
          <MarkReadButton
            onClick={() => onDismiss(activity.id)}
            label={t('card.markAsRead')}
            className="absolute top-2 right-2"
          />
        )}
      </div>
    </div>
    </SwipeToRead>
  );
};
